// src/features/auth/hooks/use-guidance-animation.ts
import { useState, useRef, useCallback, useEffect } from "react";

interface UseGuidanceAnimationOptions {
  /** Delay (ms) after page mount before auto-triggering guidance */
  autoTriggerDelay?: number;
  /** How long the guidance stays visible (ms) */
  duration?: number;
}

interface UseGuidanceAnimationReturn {
  isGuidanceActive: boolean;
  triggerGuidance: () => void;
  resetGuidance: () => void;
}

export function useArrowAnimation({
  autoTriggerDelay = 1400,
  duration = 3200,
}: UseGuidanceAnimationOptions = {}): UseGuidanceAnimationReturn {
  const [isGuidanceActive, setIsGuidanceActive] = useState(false);
  const durationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (durationTimer.current) clearTimeout(durationTimer.current);
    if (autoTimer.current) clearTimeout(autoTimer.current);
  }, []);

  const triggerGuidance = useCallback(() => {
    clearTimers();
    setIsGuidanceActive(true);
    durationTimer.current = setTimeout(
      () => setIsGuidanceActive(false),
      duration,
    );
  }, [clearTimers, duration]);

  const resetGuidance = useCallback(() => {
    clearTimers();
    setIsGuidanceActive(false);
  }, [clearTimers]);

  // Auto-trigger after page entrance animations settle
  useEffect(() => {
    autoTimer.current = setTimeout(triggerGuidance, autoTriggerDelay);
    return clearTimers;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { isGuidanceActive, triggerGuidance, resetGuidance };
}
